import React, { useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import {
  CourseHourChangeRequestWithUsers,
  HourChangeRequestStatusType,
} from '../../../../../api/types/programs/course/course-hours-request-change/course-hours-request-change.type';

interface CourseHoursHistoryDialogProps {
  visible: boolean;
  onHide: () => void;
  historyData: CourseHourChangeRequestWithUsers[];
  onStatusUpdate: (
    requestId: number,
    status: HourChangeRequestStatusType,
  ) => Promise<void>;
  onEditRequest: (request: CourseHourChangeRequestWithUsers) => void;
}

const CourseHoursHistoryDialog: React.FC<CourseHoursHistoryDialogProps> = ({
  visible,
  onHide,
  historyData,
  onStatusUpdate,
  onEditRequest,
}) => {
  const [loadingRows, setLoadingRows] = useState<Set<number>>(new Set());
  const getStatusSeverity = (status: HourChangeRequestStatusType) => {
    switch (status) {
      case HourChangeRequestStatusType.APPROVED:
        return 'success';
      case HourChangeRequestStatusType.DENIED:
        return 'danger';
      case HourChangeRequestStatusType.PENDING:
        return 'warning';
      default:
        return 'info';
    }
  };

  const getStatusLabel = (status: HourChangeRequestStatusType) => {
    switch (status) {
      case HourChangeRequestStatusType.APPROVED:
        return 'אושר';
      case HourChangeRequestStatusType.DENIED:
        return 'נדחה';
      case HourChangeRequestStatusType.PENDING:
        return 'ממתין';
      default:
        return status;
    }
  };

  const statusBodyTemplate = (rowData: CourseHourChangeRequestWithUsers) => {
    return (
      <Tag
        value={getStatusLabel(rowData.status)}
        severity={getStatusSeverity(rowData.status)}
      />
    );
  };

  const requesterBodyTemplate = (rowData: CourseHourChangeRequestWithUsers) => {
    return `${rowData.creatorFirstName} ${rowData.creatorLastName}`;
  };

  const dateBodyTemplate = (rowData: CourseHourChangeRequestWithUsers) => {
    return new Date(rowData.createdAt).toLocaleDateString('he-IL');
  };

  const hoursBodyTemplate = (rowData: CourseHourChangeRequestWithUsers) => {
    return `+${rowData.requestedAdditionalHours}`;
  };

  const handleStatusUpdate = async (
    requestId: number,
    status: HourChangeRequestStatusType,
  ) => {
    setLoadingRows((prev) => new Set(prev).add(requestId));
    try {
      await onStatusUpdate(requestId, status);
    } catch (error) {
      console.error('Error updating status:', error);
    } finally {
      setLoadingRows((prev) => {
        const newSet = new Set(prev);
        newSet.delete(requestId);
        return newSet;
      });
    }
  };

  const actionsBodyTemplate = (rowData: CourseHourChangeRequestWithUsers) => {
    if (rowData.status !== HourChangeRequestStatusType.PENDING) {
      return null;
    }

    const isLoading = loadingRows.has(rowData.id);

    return (
      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
        <Button
          icon="pi pi-pencil"
          severity="info"
          size="small"
          outlined
          disabled={isLoading}
          onClick={() => onEditRequest(rowData)}
          tooltip="ערוך בקשה"
          tooltipOptions={{ position: 'top' }}
          style={{ width: '32px', height: '32px' }}
        />
        <Button
          icon="pi pi-check"
          severity="success"
          size="small"
          outlined
          loading={isLoading}
          disabled={isLoading}
          onClick={() =>
            handleStatusUpdate(rowData.id, HourChangeRequestStatusType.APPROVED)
          }
          tooltip="אשר בקשה"
          tooltipOptions={{ position: 'top' }}
          style={{ width: '32px', height: '32px' }}
        />
        <Button
          icon="pi pi-times"
          severity="danger"
          size="small"
          outlined
          loading={isLoading}
          disabled={isLoading}
          onClick={() =>
            handleStatusUpdate(rowData.id, HourChangeRequestStatusType.DENIED)
          }
          tooltip="דחה בקשה"
          tooltipOptions={{ position: 'top' }}
          style={{ width: '32px', height: '32px' }}
        />
      </div>
    );
  };

  // Sort by oldest first (creation date)
  const sortedData = [...historyData].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );

  return (
    <Dialog
      header="היסטוריית בקשות שינוי שעות הקורס"
      visible={visible}
      onHide={onHide}
      style={{ width: '800px' }}
      modal
    >
      <div style={{ direction: 'rtl' }}>
        {sortedData.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
            אין היסטוריית בקשות
          </div>
        ) : (
          <DataTable
            value={sortedData}
            responsiveLayout="scroll"
            emptyMessage="אין נתונים להצגה"
          >
            <Column
              field="createdAt"
              header="תאריך בקשה"
              body={dateBodyTemplate}
              style={{ width: '15%', textAlign: 'right' }}
            />
            <Column
              header="מבקש"
              body={requesterBodyTemplate}
              style={{ width: '20%', textAlign: 'right' }}
            />
            <Column
              field="requestedAdditionalHours"
              header="שעות מבוקשות"
              body={hoursBodyTemplate}
              style={{ width: '15%', textAlign: 'right' }}
            />
            <Column
              field="justification"
              header="הצדקה"
              style={{ width: '25%', textAlign: 'right' }}
            />
            <Column
              field="status"
              header="סטטוס"
              body={statusBodyTemplate}
              style={{ width: '10%', textAlign: 'right' }}
            />
            <Column
              header="פעולות"
              body={actionsBodyTemplate}
              style={{ width: '15%', textAlign: 'center' }}
            />
          </DataTable>
        )}
      </div>
    </Dialog>
  );
};

export default CourseHoursHistoryDialog;
